import { Component, Inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '@app/material.module';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { ShippingsService } from '@app/shared/services/entities/shippings.service';

export interface ReceptionRfidDialogData {
  shipping: any;
  productsById: Record<string, any>;
  warehouseName: string;
}

interface UnitLine {
  line: string;          // "<renglón>-<unidad>"
  product: string;
  code: string;
  description: string;
  batch: string | null;
  expiration: string | null;
  tagRfid: string;
  locked: boolean;       // ya tiene pieza creada (está en `rfid` del documento)
}

/**
 * Asociar etiquetas RFID a una entrada de material.
 *
 * Cada renglón de la entrada se abre en una línea por unidad (insumo x
 * cantidad). Al guardar, cada línea con etiqueta crea su pieza en el locker de
 * la entrada. Las que ya tienen pieza aparecen bloqueadas: se puede ir por
 * partes sin duplicar nada.
 */
@Component({
  standalone: true,
  selector: 'app-reception-rfid-dialog',
  imports: [FormsModule, MaterialModule],
  template: `
    <h2 mat-dialog-title class="rf-title">
      <mat-icon>contactless</mat-icon>
      RFID · {{ data.shipping.fileNumber }} — {{ data.warehouseName }}
    </h2>
    <mat-dialog-content>
      <p class="hint">
        Una línea por pieza. Escribe o escanea la etiqueta (24 caracteres) y guarda: cada línea etiquetada entra al
        locker <b>{{ data.warehouseName }}</b> con el No. de delivery <b>{{ data.shipping.deliveryFolio || '—' }}</b>.
        Las líneas ya etiquetadas no se vuelven a crear.
      </p>

      <div class="scan">
        <mat-form-field class="scan__field">
          <mat-label>Escanear etiqueta RFID (24 caracteres) — llena la siguiente línea pendiente</mat-label>
          <input matInput [(ngModel)]="scanValue" maxlength="24" (keydown.enter)="scanNext()" autocomplete="off">
          <mat-icon matSuffix>contactless</mat-icon>
          <mat-hint align="end">{{ (scanValue || '').length }}/24</mat-hint>
        </mat-form-field>
        <span class="progress"><b>{{ taggedCount }}</b> de {{ lines.length }} etiquetadas</span>
      </div>

      <table class="rows">
        <tr><th>#</th><th>Código</th><th>Descripción</th><th>Lote</th><th>Caducidad</th><th>Etiqueta RFID (24 caracteres)</th></tr>
        @for (l of lines; track l.line; let i = $index) {
          <tr [class.done]="l.locked">
            <td>{{ i + 1 }}</td>
            <td>{{ l.code }}</td>
            <td>{{ l.description }}</td>
            <td>{{ l.batch || 'S/L' }}</td>
            <td>{{ l.expiration || '—' }}</td>
            <td>
              @if (l.locked) {
                <span class="tag-ok"><mat-icon>check_circle</mat-icon>{{ l.tagRfid }}</span>
              } @else {
                <input class="cell" [(ngModel)]="l.tagRfid" maxlength="24" placeholder="24 caracteres">
              }
            </td>
          </tr>
        }
      </table>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="close()">Cerrar</button>
      @if (allTagged) {
        <button mat-raised-button color="primary" type="button" (click)="close()">
          <mat-icon>task_alt</mat-icon> Terminar ({{ taggedCount }} de {{ lines.length }})
        </button>
      } @else {
        <button mat-raised-button color="accent" type="button" (click)="save()" [disabled]="saving || pendingWithTag === 0">
          <mat-icon>save</mat-icon> Guardar {{ pendingWithTag }} etiqueta(s)
        </button>
      }
    </mat-dialog-actions>
  `,
  styles: [`
    .rf-title { display: flex; align-items: center; gap: 8px; }
    .hint { font-size: 13px; color: #555; border-left: 3px solid #1010eb; background: #f4f6ff; padding: 6px 10px; border-radius: 4px; }
    .scan { display: flex; flex-wrap: wrap; align-items: center; gap: 0 16px; }
    .scan__field { flex: 1 1 320px; }
    .progress { font-size: 13px; color: #455a64; }
    .rows { width: 100%; border-collapse: collapse; font-size: 13px; }
    .rows th, .rows td { padding: 6px 8px; border-bottom: 1px solid #eceff1; text-align: left; }
    .rows tr.done td { background: #f1f8e9; }
    .cell { width: 100%; min-width: 200px; padding: 4px 6px; border: 1px solid #cfd8dc; border-radius: 4px; font-family: monospace; }
    .tag-ok { display: inline-flex; align-items: center; gap: 4px; color: #2e7d32; font-family: monospace; }
    .tag-ok mat-icon { font-size: 16px; width: 16px; height: 16px; }
  `],
})
export class ReceptionRfidDialogComponent {
  lines: UnitLine[] = [];
  scanValue = '';
  saving = false;
  changed = false;

  constructor(
    public dialogRef: MatDialogRef<ReceptionRfidDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ReceptionRfidDialogData,
    private shippingsService: ShippingsService,
    private customMessageService: CustomMessageService,
  ) {
    this.buildLines();
  }

  private buildLines(): void {
    const done = new Map<string, any>((this.data.shipping.rfid ?? []).map((x: any) => [String(x.line), x]));
    const items: any[] = Array.isArray(this.data.shipping.items) ? this.data.shipping.items : [];
    const lines: UnitLine[] = [];
    items.forEach((it, i) => {
      const prod = this.data.productsById[it.product] ?? {};
      const qty = Math.max(0, Number(it.quantity) || 0);
      for (let n = 0; n < qty; n++) {
        const key = `${i}-${n}`;
        const prev = done.get(key);
        lines.push({
          line: key, product: it.product,
          code: prod.code ?? '—', description: prod.description ?? '—',
          batch: it.batch ?? null, expiration: it.expiration ?? null,
          tagRfid: prev?.tagRfid ?? '', locked: !!prev,
        });
      }
    });
    this.lines = lines;
  }

  get taggedCount(): number { return this.lines.filter(l => l.locked).length; }
  /** Todas las piezas ya tienen etiqueta: la recepción quedó completa. */
  get allTagged(): boolean { return this.lines.length > 0 && this.taggedCount === this.lines.length; }

  /** Cierra el diálogo (y cualquier aviso que se hubiera quedado abierto encima). */
  close(): void {
    this.customMessageService.close();
    this.dialogRef.close(this.changed);
  }
  get pendingWithTag(): number { return this.lines.filter(l => !l.locked && l.tagRfid.trim()).length; }

  scanNext(): void {
    const tag = this.scanValue.trim().toUpperCase();
    this.scanValue = '';
    if (!tag) return;
    if (tag.length !== 24) {
      void this.customMessageService.open({ title: 'Etiqueta inválida', type: 'warning', message: `La etiqueta <b>${tag}</b> tiene ${tag.length} caracteres; debe tener exactamente 24.`, dismissBtn: true });
      return;
    }
    if (this.lines.some(l => l.tagRfid.trim().toUpperCase() === tag)) {
      void this.customMessageService.open({ title: 'Información', type: 'neutral', message: `La etiqueta <b>${tag}</b> ya está en la lista.`, duration: 2000 });
      return;
    }
    const next = this.lines.find(l => !l.locked && !l.tagRfid.trim());
    if (next) next.tagRfid = tag;
  }

  async save(): Promise<void> {
    const pending = this.lines.filter(l => !l.locked && l.tagRfid.trim());
    const bad = pending.find(l => l.tagRfid.trim().length !== 24);
    if (bad) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: `La etiqueta <b>${bad.tagRfid}</b> debe tener 24 caracteres.`, dismissBtn: true });
      return;
    }
    const tags = pending.map(l => l.tagRfid.trim().toUpperCase());
    const dup = tags.find((t, i) => tags.indexOf(t) !== i);
    if (dup) {
      await this.customMessageService.open({ title: 'Advertencia', type: 'warning', message: `La etiqueta <b>${dup}</b> está repetida.`, dismissBtn: true });
      return;
    }
    this.saving = true;
    this.customMessageService.open({ title: 'Espere', type: 'neutral', message: 'Creando piezas en el locker...', loading: true });
    try {
      const response: any = await this.shippingsService.tagReception({
        shipping: this.data.shipping.id,
        lines: pending.map(l => ({ line: l.line, product: l.product, batch: l.batch, expiration: l.expiration, tagRfid: l.tagRfid.trim().toUpperCase() })),
      });
      this.customMessageService.close();
      const result = response?.data?.[0] ?? response?.data ?? {};
      if (Array.isArray(result.rfid)) {
        this.data.shipping.rfid = result.rfid;
        this.buildLines();
      }
      this.changed = true;
      const created = result.created ?? pending.length;
      if (this.allTagged) {
        // Recepción completa: se cierra sola y se avisa en la bitácora.
        this.dialogRef.close(true);
        void this.customMessageService.open({ title: '¡Recepción completa!', type: 'success', message: `${created} pieza(s) agregadas al locker. Todas las piezas de ${this.data.shipping.fileNumber} ya tienen etiqueta.`, duration: 2500 });
        return;
      }
      void this.customMessageService.open({ title: '¡Hecho!', type: 'success', message: `${created} pieza(s) agregadas al locker.`, duration: 2200 });
    } catch (err) {
      this.customMessageService.close();
      await this.customMessageService.open({ title: 'Error', type: 'error', message: String(err), dismissBtn: true });
    } finally {
      this.saving = false;
    }
  }
}
